import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

export interface RoleRightsGroupDefinition {
    id: string;
    title: ReactNode;
    keys: string[];
}

/**
 * Permission entity keys grouped like the platform left-side navigation / admin domains.
 * Each key is assigned to exactly one group; unmapped keys fall into "Other".
 */
export const ROLE_RIGHTS_GROUPS: RoleRightsGroupDefinition[] = [
    {
        id: 'users',
        title: getApplicationDisplayName(Application.USERS),
        keys: ['user', 'user_group', 'invite', 'certificate', 'user_certificate', 'custom_role', 'role', 'identification', 'badge']
    },
    {
        id: 'address_book',
        title: getApplicationDisplayName(Application.ADDRESS_BOOK),
        keys: ['contact', 'contact_group', 'address']
    },
    {
        id: 'organization',
        title: CommonMessage.NAVIGATION.ORGANIZATION,
        keys: ['account', 'branding', 'logo', 'functionality', 'wizard', 'wizard_step', 'setting', 'sso_provider', 'seed', 'license', 'bundle']
    },
    {
        id: 'spots',
        title: getApplicationDisplayName(Application.SPOTS),
        keys: ['spot', 'module', 'slot', 'module_template', 'slot_template', 'template', 'playlist', 'playlist_item']
    },
    {
        id: 'catalogue',
        title: getApplicationDisplayName(Application.CATALOGUE),
        keys: [
            'product',
            'product_category',
            'product_group',
            'product_kit',
            'attribute',
            'attribute_value',
            'product_attribute',
            'product_category_attribute',
            'supplier'
        ]
    },
    {
        id: 'inventory',
        title: CommonMessage.NAVIGATION.INVENTORY,
        keys: ['stock', 'stock_movement', 'movement', 'basket', 'basket_item', 'grouped_stock_aggregation', 'slot_stock_aggregation']
    },
    {
        id: 'equipment',
        title: CommonMessage.NAVIGATION.EQUIPMENT,
        keys: [
            'equipment',
            'equipment_group',
            'equipment_variable',
            'equipment_timer',
            'equipment_count',
            'equipment_use',
            'equipment_certificate',
            'equipment_problem',
            'problem',
            'problem_entity'
        ]
    },
    {
        id: 'hardware',
        title: getApplicationDisplayName(Application.HARDWARE),
        keys: ['iot', 'device', 'locker', 'sensor', 'actuator', 'iot_troubleshoot']
    },
    {
        id: 'transactions',
        title: CommonMessage.NAVIGATION.TRANSACTIONS,
        keys: [
            'transaction',
            'transaction_actor',
            'transaction_user',
            'transaction_attachment',
            'transaction_timer',
            'transaction_count',
            'package',
            'package_timer',
            'package_variable',
            'package_pin_code',
            'colli',
            'colli_content',
            'colli_timer',
            'colli_actor',
            'colli_user',
            'pin_code',
            'review',
            'review_transaction'
        ]
    },
    {
        id: 'shop',
        title: getApplicationDisplayName(Application.SHOP),
        keys: ['order', 'order_item']
    },
    {
        id: 'budgets',
        title: getApplicationDisplayName(Application.BUDGETS),
        keys: ['budget', 'budget_entity', 'budget_rule', 'budget_use', 'cost_center']
    },
    {
        id: 'constraints',
        title: getApplicationDisplayName(Application.CONSTRAINTS),
        keys: ['constraint']
    },
    {
        id: 'requirements',
        title: getApplicationDisplayName(Application.REQUIREMENTS),
        keys: ['stock_requirement', 'requirement', 'certificate_requirement']
    },
    {
        id: 'analytics',
        title: getApplicationDisplayName(Application.ANALYTICS),
        keys: ['report', 'report_job', 'report_schedule', 'schedule']
    },
    {
        id: 'notifications',
        title: getApplicationDisplayName(Application.NOTIFICATIONS),
        keys: ['notification', 'notification_template', 'notification_email', 'notification_content', 'receiver', 'inbox_message', 'outbox_message']
    },
    {
        id: 'content_and_media',
        title: CommonMessage.NAVIGATION.CONTENT_AND_MEDIA,
        keys: [
            'question',
            'questionnaire',
            'answer',
            'document',
            'document_entity',
            'media_file',
            'label',
            'label_content',
            'label_template',
            'label_template_content'
        ]
    },
    {
        id: 'workflows',
        title: CommonMessage.OBJECTS.WORKFLOW.PLURAL,
        keys: ['workflow', 'workflow_step', 'workflow_field', 'user_interface', 'user_interface_button']
    },
    {
        id: 'integrations',
        title: (
            <FormattedMessage
                id='platform.role.role-rights.group.integrations'
                description='Group title for integration-related role rights'
                defaultMessage='Integrations'
            />
        ),
        keys: ['barcode', 'payment', 'blob', 'callback', 'external_entity_mapping', 'impex', 'import', 'import_job', 'import_job_error', 'import_job_lock']
    },
    {
        id: 'system',
        title: (
            <FormattedMessage
                id='platform.role.role-rights.group.system'
                description='Group title for system and audit-related role rights'
                defaultMessage='System'
            />
        ),
        keys: ['event', 'audit', 'audit_log', 'change', 'docker_image', 'authorization', 'permission']
    }
];

export const ROLE_RIGHTS_OTHER_GROUP_ID = 'other';

export const ROLE_RIGHTS_OTHER_TITLE = (
    <FormattedMessage
        id='platform.role.role-rights.group.other'
        description='Group title for role rights that do not belong to a known domain'
        defaultMessage='Other'
    />
);

export function getPermissionGroupId(entityKey: string): string {
    const group = ROLE_RIGHTS_GROUPS.find((definition) => definition.keys.includes(entityKey));
    return group?.id ?? ROLE_RIGHTS_OTHER_GROUP_ID;
}
